import React, { useEffect, useState } from "react";
import { EmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import {
  EmployeeWorkOrderPayRateRecord,
  EmployeeWorkOrderPayRateResponse,
} from "../interfaces/WorkOrderPayRate";
import {
  getEmployeeWorkOrderPayRate,
  postEmployeeWorkOrderPayRate,
  revertEmployeeWorkOrderPayRate,
} from "../api/workOrderPayRates";
import { getApiErrorMessage } from "../api/adminManagement";
import { DollarSign, Clock, X, Check, Info, AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Work Order pay rate assignment — v2 modal (Employees page, TAS-290).
 * Rendered inside the page's `.emp` scope, reusing the shared emp-modal / emp-to-*
 * classes from EmployeeView.css (same structure as EmployeePWClassificationModal:
 * identity card → current card (with system rate alongside) → history timeline →
 * effective-dated change form). $35 cap is enforced client-side and server-side.
 */

/** Hard cap mirrored from the API (WORK_ORDER_RATE_CAP). A future cap change is a
 *  constant edit, not a migration — see API TAS-290 plan Part 1. */
export const WORK_ORDER_RATE_CAP = 35;

interface EmployeeWorkOrderRateModalProps {
  isOpen: boolean;
  employee: EmployeeInfo;
  /** Whether the current admin may change the rate (manageWorkOrderRates). When
   *  false the modal renders read-only (current + history, no change form). */
  canManage: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function initials(first: string, last: string): string {
  return `${(first || "?").charAt(0)}${(last || "").charAt(0)}`.toUpperCase();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "YYYY-MM-DD"/ISO → "Jan 1, 2026" without timezone shift. */
function fmtISO(dateStr: string | null): string {
  if (!dateStr) return "—";
  const p = dateStr.slice(0, 10).split("-");
  if (p.length !== 3) return dateStr;
  const mo = parseInt(p[1], 10) - 1;
  if (mo < 0 || mo > 11) return dateStr;
  return `${MONTHS[mo]} ${parseInt(p[2], 10)}, ${p[0]}`;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtRate(rate: number): string {
  return `$${rate.toFixed(2)}`;
}

const EmployeeWorkOrderRateModal: React.FC<EmployeeWorkOrderRateModalProps> = ({
  isOpen,
  employee,
  canManage,
  onClose,
  onSaved,
}) => {
  const [current, setCurrent] = useState<EmployeeWorkOrderPayRateRecord | null>(null);
  const [history, setHistory] = useState<EmployeeWorkOrderPayRateRecord[]>([]);
  const [systemRate, setSystemRate] = useState<number>(0);
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [rateError, setRateError] = useState<string | null>(null);
  const [effectiveStartDate, setEffectiveStartDate] = useState<string>("");
  const [dateError, setDateError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmingReset, setConfirmingReset] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEffectiveStartDate(todayISO());
    setHourlyRate("");
    setError(null);
    setRateError(null);
    setDateError(false);
    setDataFetched(false);
    setLoading(true);
    getEmployeeWorkOrderPayRate(employee.employeeID)
      .then((data: EmployeeWorkOrderPayRateResponse) => {
        setCurrent(data.current);
        setHistory(data.history);
        setSystemRate(data.systemRate);
        setHourlyRate((data.current ? data.current.hourlyRate : data.systemRate).toFixed(2));
        setDataFetched(true);
      })
      .catch(() => setError("Failed to load Work Order pay rate data."))
      .finally(() => setLoading(false));
  }, [isOpen, employee.employeeID]);

  // Esc closes (matching the page's other v2 modals).
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Allow only numbers and one decimal point (ModifiedDutyModal pattern).
    value = value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + "." + parts[1].substring(0, 2);
    }

    setHourlyRate(value);
    setRateError(null);
  };

  const validateRate = (): number | null => {
    const rate = parseFloat(hourlyRate);
    if (isNaN(rate) || rate <= 0) {
      setRateError("Enter a valid hourly rate greater than $0.00.");
      return null;
    }
    if (rate > WORK_ORDER_RATE_CAP) {
      setRateError(`Hourly rate cannot exceed $${WORK_ORDER_RATE_CAP.toFixed(2)}.`);
      return null;
    }
    return Math.round(rate * 100) / 100;
  };

  const handleSave = async () => {
    if (!effectiveStartDate) {
      setDateError(true);
      return;
    }
    const rate = validateRate();
    if (rate === null) return;
    setSaving(true);
    setError(null);
    try {
      await postEmployeeWorkOrderPayRate(employee.employeeID, {
        hourlyRate: rate,
        effectiveStartDate,
      });
      await onSaved(); // parent refreshes the roster and closes the modal
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save. Please try again."));
      setSaving(false);
    }
  };

  // TAS-292 follow-up — one click back to the system rate. Two-step confirm,
  // effective today; the server closes (or same-day deletes) the open row and
  // re-syncs any unsubmitted claims.
  const handleReset = async () => {
    if (!confirmingReset) {
      setConfirmingReset(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await revertEmployeeWorkOrderPayRate(employee.employeeID, todayISO());
      await onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to reset. Please try again."));
      setConfirmingReset(false);
      setSaving(false);
    }
  };

  const isBackdated = !!(
    current &&
    effectiveStartDate &&
    effectiveStartDate < current.effectiveStartDate.slice(0, 10)
  );
  // Timeline = the open (current) record first, then closed history records.
  const timelineItems = [...(current ? [current] : []), ...history];

  return (
    <div className="emp-modal-bg" onClick={() => !saving && onClose()}>
      <div className="emp-modal emp-modal-wide" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="emp-modal-head">
          <div className="mi">
            <DollarSign size={21} />
          </div>
          <div>
            <h3>Work Order Pay Rate</h3>
            <div className="ms">
              {employee.firstName} {employee.lastName} · {employee.altEmployeeID || "—"}
            </div>
          </div>
          <button type="button" className="x" onClick={onClose} disabled={saving}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="emp-modal-body">
          {/* Identity card */}
          <div className="emp-confirm-emp">
            <span className="av">{initials(employee.firstName, employee.lastName)}</span>
            <div>
              <div className="nm">
                {employee.firstName} {employee.lastName}
              </div>
              <div className="who-sub emp-mono">
                {employee.altEmployeeID || "—"}
                {employee.jobTitle ? ` · ${employee.jobTitle}` : ""}
              </div>
            </div>
          </div>

          {loading && (
            <div className="emp-to-loading">
              <span className="emp-spin" /> Loading pay rate data…
            </div>
          )}

          {error && (
            <div className="emp-to-warn-banner">
              <AlertTriangle size={17} />
              <div>{error}</div>
            </div>
          )}

          {!loading && dataFetched && (
            <>
              {/* System rate + current override, side by side */}
              <div className="emp-to-subhead">Current rate</div>
              <div className="emp-to-frow2">
                <div className="emp-to-cur-card" style={{ marginBottom: 0 }}>
                  <div className="emp-to-cur-top">
                    <span className="emp-to-cur-ic">
                      <DollarSign size={18} />
                    </span>
                    <div className="emp-to-cur-body">
                      <div className="emp-to-cur-name">System (PAN) rate</div>
                      <div className="emp-to-cur-meta">
                        <span className="emp-to-cur-since">{fmtRate(systemRate)}/hr</span>
                      </div>
                    </div>
                  </div>
                </div>
                {current ? (
                  <div className="emp-to-cur-card" style={{ marginBottom: 0 }}>
                    <div className="emp-to-cur-top">
                      <span className="emp-to-cur-ic">
                        <DollarSign size={18} />
                      </span>
                      <div className="emp-to-cur-body">
                        <div className="emp-to-cur-name">{fmtRate(current.hourlyRate)}/hr override</div>
                        <div className="emp-to-cur-meta">
                          <span className="emp-to-cur-since">
                            <Clock size={13} />
                            Since {fmtISO(current.effectiveStartDate)}
                            {current.setByName ? ` · Set by ${current.setByName}` : ""}
                          </span>
                        </div>
                      </div>
                      <span className="emp-cur-badge">
                        <span className="dot" />
                        Current
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="emp-to-empty" style={{ marginBottom: 0 }}>
                    <Info size={19} />
                    <div>
                      <div className="emp-to-empty-t">No override set</div>
                      <div className="emp-to-empty-s">
                        Work Order claims use the system rate until an override is set below.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* History timeline */}
              {history.length > 0 && (
                <>
                  <div className="emp-to-divider" />
                  <div className="emp-to-subhead">Rate history</div>
                  <div className="emp-to-timeline">
                    {timelineItems.map((item) => {
                      const open = item.effectiveEndDate === null;
                      return (
                        <div
                          key={item.employeeWorkOrderPayRateID}
                          className={`emp-to-tl-item${open ? " cur" : ""}`}
                        >
                          <span className="emp-to-tl-dot" />
                          <div className="emp-to-tl-body">
                            <div className="emp-to-tl-head">
                              <span className="emp-to-tl-name">{fmtRate(item.hourlyRate)}/hr</span>
                              {open && (
                                <span className="emp-cur-badge">
                                  <span className="dot" />
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="emp-to-tl-range emp-mono">
                              {fmtISO(item.effectiveStartDate)} →{" "}
                              {open ? (
                                <span className="emp-to-tl-present">Present</span>
                              ) : (
                                fmtISO(item.effectiveEndDate)
                              )}
                            </div>
                            {item.setByName && <div className="emp-to-tl-notes">Set by {item.setByName}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Change form (only when the admin can manage rates) */}
              {canManage && (
                <>
                  <div className="emp-to-divider" />
                  <div className="emp-to-subhead">{current ? "Change rate" : "Set override rate"}</div>

                  <div className="emp-to-frow2">
                    <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                      <label>
                        Hourly rate <span className="emp-req">*</span>
                      </label>
                      <input
                        type="text"
                        className={`emp-input${rateError ? " emp-to-err" : ""}`}
                        placeholder="0.00"
                        value={hourlyRate}
                        onChange={handleRateChange}
                        disabled={saving}
                      />
                      {rateError && (
                        <div className="emp-to-inline-err">
                          <AlertTriangle size={15} />
                          {rateError}
                        </div>
                      )}
                    </div>
                    <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                      <label>
                        Effective date <span className="emp-req">*</span>
                      </label>
                      <input
                        type="date"
                        className={`emp-input${dateError ? " emp-to-err" : ""}`}
                        value={effectiveStartDate}
                        onChange={(e) => {
                          setEffectiveStartDate(e.target.value);
                          if (e.target.value) setDateError(false);
                        }}
                        disabled={saving}
                      />
                      {dateError && (
                        <div className="emp-to-inline-err">
                          <AlertTriangle size={15} />
                          An effective date is required.
                        </div>
                      )}
                    </div>
                  </div>

                  {isBackdated && (
                    <div className="emp-to-warn-banner">
                      <AlertTriangle size={17} />
                      <div>
                        This date is <b>before</b> the current rate began (
                        {fmtISO(current!.effectiveStartDate)}). Backdating changes which rate applies to
                        already-worked days — double-check the effective date.
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="emp-modal-foot">
          {dataFetched && canManage && current && (
            <button
              type="button"
              className="emp-btn"
              style={{ marginRight: "auto", color: confirmingReset ? "#a8331f" : undefined }}
              onClick={handleReset}
              disabled={saving}
              title={`Remove the override — Work Order claims go back to the system rate (${fmtRate(systemRate)}/hr) from today`}
            >
              <RotateCcw size={15} />
              {confirmingReset ? "Confirm reset to system rate" : "Reset to system rate"}
            </button>
          )}
          <button type="button" className="emp-btn" onClick={onClose} disabled={saving}>
            {dataFetched && canManage ? "Cancel" : "Close"}
          </button>
          {dataFetched && canManage && (
            <button
              type="button"
              className="emp-btn primary"
              onClick={handleSave}
              disabled={saving || !hourlyRate}
            >
              {saving ? (
                <>
                  <span className="emp-spin" /> Saving…
                </>
              ) : (
                <>
                  <Check size={16} />
                  {current ? "Save rate" : "Set rate"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeWorkOrderRateModal;
